import { useEffect, useState } from "react";
import { MessageSquare, Feather, Users, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * InterviewPrep — 'What tutors look for' + interview format + signals
 * panel on the Admission tab. Reads
 * `/api/universities/{id}/interview-prep`. Sourced from a curated
 * per-uni seed; country default fallback for uncurated unis.
 *
 * Hides gracefully when both the curated and default sources are
 * empty (e.g. a country we haven't defaulted yet).
 */
export default function InterviewPrep({ uniId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/interview-prep`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  const p = data?.prep;
  if (!p) return null;

  const isCurated = p.variant === "curated";

  return (
    <section className="mb-8" data-testid="interview-prep">
      <div>
        <TabKicker>03 · What Tutors Look For</TabKicker>
        <TabSectionTitle>
          Personal statement + interview signals
          {!isCurated && (
            <span className="ml-2 font-mono text-xs uppercase tracking-widest text-[#B45309]/80 align-middle">
              · country default
            </span>
          )}
        </TabSectionTitle>
      </div>

      <div className="border border-[#002147]/10 bg-white/40 p-5 md:p-6">
        {/* Personal statement bullets */}
        {p.personal_statement?.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-1.5 mb-3">
              <Feather className="h-3.5 w-3.5 text-[#7C3AED]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED]">
                Personal statement · what to signal
              </span>
            </div>
            <ul className="space-y-2 pl-1">
              {p.personal_statement.map((s, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="font-mono text-[10px] text-[#002147]/40 mt-1 shrink-0">0{i + 1}</span>
                  <span className="font-serif text-[15px] leading-relaxed text-[#002147]">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Interview format */}
        {p.interview_format && (
          <div className="mb-5 pt-4 border-t border-[#002147]/10">
            <div className="flex items-center gap-1.5 mb-2">
              <MessageSquare className="h-3.5 w-3.5 text-[#7C3AED]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED]">
                Interview format
              </span>
            </div>
            <p className="font-serif text-[15px] leading-relaxed text-[#002147]">
              {p.interview_format}
            </p>
          </div>
        )}

        {/* Interview signals */}
        {p.interview_signals?.length > 0 && (
          <div className="mb-5 pt-4 border-t border-[#002147]/10">
            <div className="flex items-center gap-1.5 mb-3">
              <Users className="h-3.5 w-3.5 text-[#7C3AED]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED]">
                What interviewers reward
              </span>
            </div>
            <ul className="space-y-2 pl-1">
              {p.interview_signals.map((s, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="font-mono text-[10px] text-[#002147]/40 mt-1 shrink-0">→</span>
                  <span className="font-sans text-sm leading-relaxed text-[#002147]/85">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sample question + source */}
        {(p.sample_question || p.source) && (
          <div className="pt-4 border-t border-[#002147]/10 flex items-baseline justify-between gap-4">
            {p.sample_question && (
              <div className="font-serif text-sm text-[#002147]/70 italic leading-relaxed min-w-0">
                <span className="font-mono not-italic text-[9px] uppercase tracking-widest text-[#002147]/40 block mb-1">
                  A recent question
                </span>
                {p.sample_question}
              </div>
            )}
            {p.source && (
              <a
                href={p.source}
                target="_blank" rel="noopener noreferrer"
                className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline inline-flex items-center gap-1"
              >
                Uni page <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
